import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { type PaginatorState } from 'primeng/paginator';

import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { CardContent } from '@/shared/components/card-content/card-content';
import { PrestadorControleAdministrativoFeatureService } from '../estatisticas-cadastro-prestador/prestador-controle-administrativo.service';
import { MedicoCadastroGeralResponse } from '@/core/api/sicom-api/registro/medico/controle-administrativo/medico-cadastro-geral.model';
import { PrestadorEstatisticaCadastroAnaliticoResponse } from '@/core/api/sicom-api/registro/prestador/controle-administrativo/prestador-estatistica-agrupada.model';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';

type LocalizacaoCodigo = 'INSCRITOS' | 'ATIVOS' | 'CAPITAL' | 'INTERIOR' | 'OUTROS' | 'SEM_ENDERECO';

@Component({
    selector: 'app-cadastro-geral-prestador',
    standalone: true,
    imports: [CommonModule, CardModule, SkeletonModule, ButtonModule, PainelLateral, CardContent, DetalhamentoPendencias],
    templateUrl: './cadastro-geral-prestador.html',
    styleUrl: '../cadastro-geral/cadastro-geral.scss'
})
export class CadastroGeralPrestador implements OnInit {
    readonly skeletons = [0, 1, 2, 3, 4, 5];

    linhas: MedicoCadastroGeralResponse[] = [];
    carregando = false;

    detalheAtivo = false;
    detalheUf: string | null = null;
    detalheCodigo: LocalizacaoCodigo | null = null;
    detalheRotulo = '';
    linhasDetalhe: PendenciaLinha[] = [];
    totalDetalhe = 0;
    firstDetalhe = 0;
    rowsDetalhe = 100;
    carregandoDetalhe = false;

    private readonly detalheChange$ = new Subject<void>();

    constructor(
        private feature: PrestadorControleAdministrativoFeatureService,
        private destroyRef: DestroyRef,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.aplicar();
    }

    aplicar(): void {
        this.voltarParaCards();
        this.carregando = true;
        this.feature
            .consultarCadastroGeralPrestador()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                // markForCheck: estado assíncrono gravado em campos não-signal sob layout OnPush não seria renderizado sem marcar o caminho como dirty.
                finalize(() => {
                    this.carregando = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (dados) => (this.linhas = dados ?? []),
                error: () => (this.linhas = [])
            });
    }

    verDetalhe(uf: string, codigo: LocalizacaoCodigo, rotulo: string): void {
        this.detalheChange$.next();
        this.detalheAtivo = true;
        this.detalheUf = uf;
        this.detalheCodigo = codigo;
        this.detalheRotulo = rotulo;
        this.firstDetalhe = 0;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.carregarDetalhe();
    }

    voltarParaCards(): void {
        this.detalheChange$.next();
        this.detalheAtivo = false;
        this.detalheUf = null;
        this.detalheCodigo = null;
        this.linhasDetalhe = [];
        this.totalDetalhe = 0;
        this.firstDetalhe = 0;
        this.carregandoDetalhe = false;
    }

    onPageChangeDetalhe(event: PaginatorState): void {
        this.detalheChange$.next();
        this.firstDetalhe = event.first ?? 0;
        this.rowsDetalhe = event.rows ?? this.rowsDetalhe;
        this.carregarDetalhe();
    }

    obterIndicadorDetalhe(): IndicadorPendencia | null {
        if (!this.detalheAtivo || !this.detalheUf) {
            return null;
        }
        return {
            id: `cadastro-geral-${this.detalheCodigo}`,
            titulo: `${this.detalheRotulo} — ${this.detalheUf}`,
            descricao: `Prestadores da UF ${this.detalheUf} no grupo ${this.detalheRotulo}.`,
            grupo: 'Situacao' as IndicadorPendencia['grupo'],
            classeGrupo: 'grupo-registro',
            quantidade: this.totalDetalhe || null
        };
    }

    private carregarDetalhe(): void {
        if (!this.detalheUf || !this.detalheCodigo) {
            return;
        }
        const uf = this.detalheUf;
        const codigo = this.detalheCodigo;
        const size = this.rowsDetalhe;
        const page = size > 0 ? Math.floor(this.firstDetalhe / size) : 0;

        this.carregandoDetalhe = true;
        this.feature
            .consultarPrestadoresEstatisticaCadastro('cadastro-geral', uf, undefined, page, size, codigo)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.detalheChange$),
                finalize(() => {
                    if (this.detalheUf === uf && this.detalheCodigo === codigo) {
                        this.carregandoDetalhe = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.detalheUf !== uf || this.detalheCodigo !== codigo) {
                        return;
                    }
                    this.linhasDetalhe = (resp?.content ?? []).map((item) => this.mapLinha(item));
                    this.totalDetalhe = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.detalheUf !== uf || this.detalheCodigo !== codigo) {
                        return;
                    }
                    this.linhasDetalhe = [];
                    this.totalDetalhe = 0;
                }
            });
    }

    private mapLinha(item: PrestadorEstatisticaCadastroAnaliticoResponse): PendenciaLinha {
        return {
            uf: item.ufSgUf ?? '',
            crm: this.formatarCnpj(item.nuCnpj),
            nome: item.nmSolicitante ?? '',
            situacao: item.dsSituacao ?? item.inSituacao ?? '',
            codigoSituacao: item.inSituacao ?? '',
            detalhe: item.informacaoComplementar ?? ''
        };
    }

    private formatarCnpj(cnpj?: string | null): string {
        const digitos = String(cnpj ?? '').replace(/\D/g, '');
        if (digitos.length !== 14) {
            return digitos || '-';
        }
        return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
    }

    percentual(parte: number, total: number): string {
        if (!total) return '0%';
        return `${Math.round((parte / total) * 100)}%`;
    }

    segmento(valor: number | undefined, total: number | undefined): number {
        return total ? ((valor ?? 0) / total) * 100 : 0;
    }

    get totais(): MedicoCadastroGeralResponse {
        return this.linhas.reduce(
            (acc, l) => ({
                ufSgUf: 'BR',
                inscritos: acc.inscritos + (l.inscritos ?? 0),
                ativos: acc.ativos + (l.ativos ?? 0),
                capital: acc.capital + (l.capital ?? 0),
                interior: acc.interior + (l.interior ?? 0),
                outrosEstados: acc.outrosEstados + (l.outrosEstados ?? 0),
                semEndereco: acc.semEndereco + (l.semEndereco ?? 0)
            }),
            { ufSgUf: 'BR', inscritos: 0, ativos: 0, capital: 0, interior: 0, outrosEstados: 0, semEndereco: 0 } as MedicoCadastroGeralResponse
        );
    }
}
